import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, InjectionToken } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, TitleStrategy, withComponentInputBinding } from '@angular/router';

import { TROUBLESHOOTING_LOGS_PROVIDER } from '@ideals/partial-views/help';
import { ClarityService } from '@ideals/services/clarity';
import { ColorSchemeService } from '@ideals/services/color-scheme';
import { provideErrorLoggers } from '@ideals/services/error-logger';
import { FeatureToggleModule } from '@ideals/services/feature-toggle';
import { provideGoogleAnalytics } from '@ideals/services/google-analytics';
import { provideHealthCheckers } from '@ideals/services/health-checker';
import { provideLogRocket } from '@ideals/services/logrocket';
import { providePosthog } from '@ideals/services/posthog';
import { LOCALIZATION_BASE_URL, provideRootTranslate } from '@ideals/services/root-translate';
import { APP_CONFIG, LOCATION } from '@ideals/types';

import { environment } from '../environments/environment';

import { baseHrefInterceptor } from './core/interceptors/base-href';
import { errorsLoggerInterceptor } from './core/interceptors/errors-logger';
import { retryOn429Interceptor } from './core/interceptors/retry-on-429';
import { serverErrorsInterceptor } from './core/interceptors/server-errors';
import { userStateErrorsInterceptor } from './core/interceptors/user-state-errors';
import { provideErrorHandler } from './core/services/error-handler';
import { ConsoleErrorLogger, LocalErrorLogger, LocalErrorLogsService, RemoteErrorLogger } from './core/services/error-logger';
import { ConnectionChecker, ServersChecker } from './core/services/health-checker';
import { LocalStorageService, StorageService } from './core/services/storage';
import { PageTitleStrategy } from './core/strategies/page-title';

import { appRoutes } from './app.routes';

const BASED_ON_BROWSER_LANGUAGE = new InjectionToken<boolean>('BASED_ON_BROWSER_LANGUAGE');

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      FeatureToggleModule.forRoot(environment.unleashConfiguration)
    ),
    provideHttpClient(
      withInterceptors([
        baseHrefInterceptor,
        errorsLoggerInterceptor,
        retryOn429Interceptor,
        serverErrorsInterceptor,
        userStateErrorsInterceptor,
      ]),
    ),
    provideAnimations(),
    provideRootTranslate(),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideErrorHandler(),
    provideGoogleAnalytics(environment.googleTagId),
    provideErrorLoggers([
      ConsoleErrorLogger,
      LocalErrorLogger,
      RemoteErrorLogger,
    ]),
    provideHealthCheckers([
      ConnectionChecker,
      ServersChecker,
    ]),
    provideLogRocket(environment.logRocketAppId),
    providePosthog(environment.posthogKey),
    {
      provide: TROUBLESHOOTING_LOGS_PROVIDER,
      useExisting: LocalErrorLogsService,
    },
    {
      provide: APP_CONFIG,
      useValue: environment,
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (location: Location) => location.origin,
      deps: [LOCATION],
    },
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategy,
    },
    {
      provide: StorageService,
      useClass: LocalStorageService,
    },
    {
      provide: BASED_ON_BROWSER_LANGUAGE,
      useValue: true,
    },
    {
      provide: LOCALIZATION_BASE_URL,
      useFactory: (document: Document) => {
        try {
          const origin = new URL(document.baseURI).origin;

          return `${origin}/`;
        } catch {
          return '/';
        }
      },
      deps: [DOCUMENT],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (clarityService: ClarityService) => {
        return () => clarityService.initialize();
      },
      deps: [ClarityService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (colorSchemeService: ColorSchemeService) => {
        return () => colorSchemeService.initialize();
      },
      deps: [ColorSchemeService],
      multi: true,
    },
  ],
};
